import { useState, type FormEvent } from 'react';
import {
  joinFromDrive,
  recover,
  rememberDevice,
  restoreFromBackupFile,
  unlock,
  type AppSession,
} from '@/services/appSession';
import { driveStore } from '@/services/syncService';
import { connectGoogle } from '@/platform/googleAuth';
import { isAllowedAccount, normalizeAccountId } from '@/platform/account';
import { useSession } from '../state/session';
import { PassphraseHint, passphraseProblem } from '../components/PassphraseHint';
import { RestoreBackup } from '../components/RestoreBackup';
import { SetupScreen } from './SetupScreen';
import styles from './LoginScreen.module.css';

const WRONG = '아이디 또는 비밀번호가 맞지 않습니다.';

function describe(e: unknown): string {
  const name = (e as Error)?.name;
  if (name === 'WrongSecretError') return WRONG;
  if (name === 'NewerDataError') return (e as Error).message;
  if (name === 'MigrationFailedError')
    return '데이터 업그레이드에 실패했습니다. 기존 데이터는 그대로 보존되어 있습니다.';
  if (name === 'NoRemoteVaultError') return '이 Google 계정에는 가져올 데이터가 없습니다.';
  if (name === 'RemoteAuthError' || name === 'GoogleAuthError')
    return (e as Error).message || 'Google 연결이 취소되었습니다.';
  return '열 수 없습니다. 네트워크를 확인한 뒤 다시 시도해 주세요.';
}

/**
 * 로그인 (아이디 + 비밀번호). 회원가입은 없다.
 * - 이 기기에 자료가 있으면: 비밀번호로 잠금 해제
 * - 처음 쓰는 기기면: 아이디·비밀번호 확인 후 Google Drive에서 가져오기 (또는 백업 파일 복원)
 * 로그인 전에는 내담자 정보·메뉴를 전혀 보여주지 않는다.
 */
export function LoginScreen({ hasVault }: { hasVault: boolean }) {
  const setSession = useSession((s) => s.setSession);
  const setAccount = useSession((s) => s.setAccountName);
  const [id, setId] = useState('');
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'login' | 'newDevice' | 'recover' | 'firstSetup'>('login');
  const [remember, setRemember] = useState(true);
  const setRemembered = useSession((s) => s.setRemembered);

  /** 로그인 완료: 아이디 표시, (선택 시) 이 기기에서 로그인 유지 */
  async function finish(s: AppSession) {
    const name = normalizeAccountId(id);
    setAccount(name);
    if (remember) await rememberDevice(s, name).catch(() => undefined);
    setRemembered(remember);
    setSession(s);
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  }

  function onLogin(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      if (!(await isAllowedAccount(id))) throw Object.assign(new Error(WRONG), { name: 'WrongSecretError' });
      if (!hasVault) return setView('newDevice');
      await finish(await unlock(pass));
    });
  }

  if (view === 'firstSetup')
    return <SetupScreen initialPass={pass} onBack={() => setView('newDevice')} onDone={(s) => void finish(s)} />;

  return (
    <div className={styles.page}>
      <main className={styles.card} aria-label="로그인">
        <div className={styles.brand}>
          <img src="/icon.svg" alt="" width={56} height={56} />
          <h1>SDCare</h1>
          <p>상담 기록 · 사례 이해 · 회기 준비</p>
        </div>

        {view === 'login' && (
          <form onSubmit={onLogin} className={styles.form}>
            <label>
              아이디
              <input value={id} onChange={(e) => setId(e.target.value)} autoComplete="username" autoFocus />
            </label>
            <label>
              비밀번호
              <input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                autoComplete="current-password"
              />
            </label>
            <label className={`check ${styles.remember}`}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> 이 기기에서
              로그인 유지 (30일)
            </label>
            {error && (
              <p role="alert" className={styles.error}>
                {error}
              </p>
            )}
            <button type="submit" className="primary" disabled={busy || !id.trim() || !pass}>
              {busy ? '확인 중…' : '로그인'}
            </button>
            {hasVault && (
              <button type="button" className="link" onClick={() => (setView('recover'), setError(null))}>
                비밀번호를 잊으셨나요? (복구 키로 열기)
              </button>
            )}
          </form>
        )}

        {view === 'newDevice' && (
          <div className={styles.form}>
            <p className={styles.note}>
              이 기기에는 아직 자료가 없습니다. 다른 기기에서 쓰던 자료를 Google Drive에서 가져옵니다.
            </p>
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await connectGoogle(); // 클릭 직후 팝업
                  await finish(await joinFromDrive(pass, driveStore()));
                })
              }
            >
              {busy ? '가져오는 중…' : 'Google Drive에서 내 자료 가져오기'}
            </button>
            {error && (
              <p role="alert" className={styles.error}>
                {error}
              </p>
            )}
            <RestoreBackup
              busy={busy}
              onRestore={(text, p) =>
                void run(async () => {
                  await finish(await restoreFromBackupFile(text, p));
                })
              }
            />
            <details>
              <summary className="small">처음 쓰는 경우 (자료가 어디에도 없음)</summary>
              <p className="muted small">
                이미 다른 기기에서 쓰고 있다면 새로 시작하지 마세요. 서로 연결되지 않는 별도 자료가 됩니다.
              </p>
              <button type="button" onClick={() => setView('firstSetup')}>
                이 기기에서 새로 시작
              </button>
            </details>
            <button type="button" className="link" onClick={() => (setView('login'), setError(null))}>
              돌아가기
            </button>
          </div>
        )}

        {view === 'recover' && <RecoverForm onBack={() => (setView('login'), setError(null))} />}

        <footer className={styles.foot}>
          모든 기록은 이 기기에서 암호화되어 저장됩니다. 공용 기기에서는 로그인 유지를 끄세요.
          <br />
          <a href="/privacy">개인정보처리방침</a> · <a href="/terms">이용약관</a>
        </footer>
      </main>
    </div>
  );
}

function RecoverForm({ onBack }: { onBack: () => void }) {
  const setSession = useSession((s) => s.setSession);
  const [key, setKey] = useState('');
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const problem = passphraseProblem(p1) ?? (p1 !== p2 ? '새 비밀번호 두 입력이 다릅니다.' : null);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    recover(key, p1)
      .then(setSession)
      .catch((e: unknown) =>
        setError((e as Error)?.name === 'WrongSecretError' ? '복구 키가 맞지 않습니다.' : describe(e)),
      )
      .finally(() => setBusy(false));
  }
  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <label>
        복구 키
        <input value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" spellCheck={false} />
      </label>
      <label>
        새 비밀번호
        <input type="password" value={p1} onChange={(e) => setP1(e.target.value)} autoComplete="new-password" />
      </label>
      <PassphraseHint value={p1} />
      <label>
        새 비밀번호 다시 입력
        <input type="password" value={p2} onChange={(e) => setP2(e.target.value)} autoComplete="new-password" />
      </label>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <button type="submit" className="primary" disabled={busy || !key}>
        {busy ? '복구 중…' : '복구 키로 열기'}
      </button>
      <button type="button" className="link" onClick={onBack}>
        돌아가기
      </button>
    </form>
  );
}
